import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CreditCard, Wallet, IndianRupee, Plus } from "lucide-react";

export default function Checkout() {
  const { cart, refreshCart } = useAuth();
  const nav = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [addrId, setAddrId] = useState("");
  const [pm, setPm] = useState("COD");
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ full_name:"", mobile:"", pincode:"", house:"", area:"", city:"", state:"", landmark:"", address_type:"Home", is_default:true });

  const items = cart?.items || [];
  const subtotal = items.reduce((a,i)=>a+(i.product?.price||0)*i.quantity,0);
  const shipping = subtotal > 999 ? 0 : 49;
  const total = subtotal + shipping;

  useEffect(() => { (async () => {
    const { data } = await api.get("/addresses");
    setAddresses(data);
    if (data.length) setAddrId((data.find(a=>a.is_default) || data[0]).address_id);
    else setShowNew(true);
  })(); }, []);

  const addAddress = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/addresses", form);
    setAddresses(prev => [...prev, data]);
    setAddrId(data.address_id);
    setShowNew(false);
    toast.success("Address saved");
  };

  const place = async () => {
    if (!addrId) return toast.error("Select an address");
    if (items.length === 0) return toast.error("Cart is empty");
    setLoading(true);
    try {
      const { data: order } = await api.post("/orders", { address_id: addrId, payment_method: pm });
      if (pm === "COD") {
        await refreshCart();
        nav(`/order-success/${order.order_id}`);
      } else {
        // Try Razorpay
        try {
          const { data: rp } = await api.post("/payments/razorpay/create", { amount: Math.round(total*100) });
          const { data: keyResp } = await api.get("/payments/razorpay/key");
          const options = {
            key: keyResp.key_id, amount: rp.amount, currency: "INR",
            name: "Stylefire", description: "Order Payment",
            order_id: rp.id,
            handler: async (res) => {
              try {
                await api.post("/payments/razorpay/verify", {
                  razorpay_order_id: res.razorpay_order_id,
                  razorpay_payment_id: res.razorpay_payment_id,
                  razorpay_signature: res.razorpay_signature,
                  order_id: order.order_id,
                });
                await refreshCart();
                nav(`/order-success/${order.order_id}`);
              } catch { toast.error("Payment verification failed"); }
            },
            theme: { color: "#004BCA" },
          };
          if (window.Razorpay) {
            new window.Razorpay(options).open();
          } else {
            const s = document.createElement("script");
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.onload = () => new window.Razorpay(options).open();
            document.body.appendChild(s);
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Online payment unavailable. Order placed with COD will be the fallback.");
          await refreshCart();
          nav(`/order-success/${order.order_id}`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not place order");
    } finally { setLoading(false); }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="font-display text-3xl font-bold mb-6">Checkout</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-6">
          <section className="bg-white border border-slate-100 rounded-2xl p-5">
            <h3 className="font-display font-bold mb-4">1. Delivery Address</h3>
            <div className="space-y-2">
              {addresses.map(a => (
                <label key={a.address_id} className={`block border rounded-xl p-3 cursor-pointer ${addrId===a.address_id?"border-[#004BCA] bg-[#004BCA]/5":"border-slate-200"}`}>
                  <input data-testid={`addr-${a.address_id}`} type="radio" name="addr" className="mr-2" checked={addrId===a.address_id} onChange={()=>setAddrId(a.address_id)} />
                  <span className="font-semibold text-sm">{a.full_name} · {a.mobile}</span>
                  <div className="text-sm text-slate-600 mt-1">{a.house}, {a.area}, {a.city}, {a.state} - {a.pincode}</div>
                </label>
              ))}
              {!showNew ? (
                <button data-testid="add-address-btn" onClick={()=>setShowNew(true)} className="inline-flex items-center gap-2 text-sm text-[#004BCA] font-semibold mt-2"><Plus size={14}/> Add new address</button>
              ) : (
                <form onSubmit={addAddress} className="mt-3 grid grid-cols-2 gap-3">
                  {["full_name","mobile","pincode","house","area","city","state","landmark"].map(k => (
                    <input key={k} required={k!=="landmark"} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} placeholder={k.replace("_"," ")} className="bg-slate-50 px-3 py-2 rounded-lg text-sm border border-slate-200" data-testid={`addr-input-${k}`}/>
                  ))}
                  <button data-testid="save-address-btn" className="col-span-2 btn-primary-sf py-2 rounded-full text-sm font-semibold">Save Address</button>
                </form>
              )}
            </div>
          </section>

          <section className="bg-white border border-slate-100 rounded-2xl p-5">
            <h3 className="font-display font-bold mb-4">2. Order Review ({items.length} items)</h3>
            {items.map((it) => (
              <div key={`${it.product_id}|${it.size||""}|${it.color||""}`} className="flex gap-3 py-2 border-t border-slate-100 first:border-0">
                <img src={it.product?.images?.[0]} className="w-14 h-14 rounded-lg object-cover" alt=""/>
                <div className="flex-1">
                  <div className="text-sm font-medium">{it.product?.title}</div>
                  <div className="text-xs text-slate-500">Qty {it.quantity}{it.size?` · ${it.size}`:""}</div>
                </div>
                <div className="text-sm font-semibold">{inr((it.product?.price||0)*it.quantity)}</div>
              </div>
            ))}
          </section>

          <section className="bg-white border border-slate-100 rounded-2xl p-5">
            <h3 className="font-display font-bold mb-4">3. Payment Method</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              <PayOption icon={IndianRupee} title="Cash on Delivery" desc="Pay when you receive" active={pm==="COD"} onClick={()=>setPm("COD")} testid="pay-cod"/>
              <PayOption icon={Wallet} title="UPI" desc="GPay, PhonePe, Paytm" active={pm==="UPI"} onClick={()=>setPm("UPI")} testid="pay-upi"/>
              <PayOption icon={CreditCard} title="Card" desc="Credit / Debit Card" active={pm==="CARD"} onClick={()=>setPm("CARD")} testid="pay-card"/>
            </div>
            {(pm==="UPI"||pm==="CARD") && (
              <p className="text-xs text-slate-500 mt-3">You will be redirected to Razorpay&apos;s secure checkout. If unavailable, COD will be used.</p>
            )}
          </section>
        </div>
        <aside className="bg-slate-50 rounded-2xl p-6 h-fit sticky top-24">
          <h3 className="font-display font-bold text-lg mb-4">Summary</h3>
          <Row k="Subtotal" v={inr(subtotal)} />
          <Row k="Shipping" v={shipping===0?"FREE":inr(shipping)} />
          <div className="border-t border-slate-200 my-3"/>
          <Row k={<span className="font-bold">Total</span>} v={<span className="font-display text-lg font-bold text-[#004BCA]">{inr(total)}</span>} />
          <button data-testid="place-order-btn" disabled={loading || !addrId || items.length===0} onClick={place} className="btn-fire w-full mt-5 py-3 rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed">{loading?"Placing…": !addrId ? "Select an address" : "Place Order"}</button>
        </aside>
      </div>
    </main>
  );
}
function Row({ k, v }) { return <div className="flex justify-between text-sm mb-2"><span className="text-slate-600">{k}</span><span>{v}</span></div>; }
function PayOption({ icon: Icon, title, desc, active, onClick, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`text-left p-4 rounded-xl border ${active?"border-[#004BCA] bg-[#004BCA]/5":"border-slate-200"}`}>
    <Icon size={18} className="text-[#004BCA]"/><div className="font-semibold text-sm mt-2">{title}</div><div className="text-xs text-slate-500">{desc}</div>
  </button>;
}
